/**
 * Phase 27.43 — Duplicate lead alert banner.
 *
 * Renders on the Lead Detail page whenever `lead.has_duplicate_alert`
 * is true. Fetches the current matches on-mount (via the phone/email
 * of the visible lead) so the list stays fresh even after the admin
 * merges or closes one of the duplicates elsewhere.
 *
 * Design notes:
 *   * Foreign duplicates (owned by another agent) surface a
 *     "Belongs to: <agent>" line so the admin can protect the original
 *     agent's business — without ever auto-reassigning.
 *   * Non-admin viewers see a redacted variant (agent name blanked,
 *     lead_id null) — the backend already enforces this; the UI just
 *     displays "another agent's book" when `foreign: true`.
 *   * A single "Dismiss" click clears `has_duplicate_alert` on the
 *     lead (soft dismiss — matches stay attached in the DB for audit).
 */
import { useEffect, useState } from 'react';
import { AlertTriangle, ExternalLink, X, ShieldAlert, GitMerge, Loader2 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { leadsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

export default function LeadDuplicateAlert({ lead, isKR, onDismiss }) {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mergeTarget, setMergeTarget] = useState(null);
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    if (!lead?.has_duplicate_alert) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await leadsAPI.duplicates({
          phone: lead.phone,
          email: lead.email,
          exclude_lead_id: lead.lead_id,
        });
        if (!cancelled) setMatches(r.data?.matches || []);
      } catch {
        // Silent — if the endpoint 500s we still render the persisted
        // list of ids below.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [lead?.lead_id, lead?.phone, lead?.email, lead?.has_duplicate_alert]);

  if (!lead?.has_duplicate_alert) return null;

  const count = matches.length || (lead.possible_duplicate_of?.length || 0);
  if (count === 0 && !loading) return null;

  // Compute the merge plan for a given row: whichever lead was
  // created first survives, the other one gets absorbed. This matches
  // the product intent ("fusionar todo en el lead más antiguo").
  const buildMergePlan = (row) => {
    if (!row?.lead_id || !lead?.lead_id) return null;
    const rowTime = new Date(row.created_at || 0).getTime();
    const curTime = new Date(lead.created_at || 0).getTime();
    const rowIsOlder = rowTime && curTime && rowTime <= curTime;
    if (rowIsOlder) {
      return {
        keeper: row,
        source: {
          lead_id: lead.lead_id,
          first_name: lead.first_name,
          last_name: lead.last_name,
        },
      };
    }
    return {
      keeper: {
        lead_id: lead.lead_id,
        first_name: lead.first_name,
        last_name: lead.last_name,
      },
      source: row,
    };
  };

  const confirmMerge = async () => {
    if (!mergeTarget) return;
    setMerging(true);
    try {
      const r = await leadsAPI.merge(mergeTarget.source.lead_id, mergeTarget.keeper.lead_id);
      const d = r.data || {};
      toast.success(
        `Leads merged — ${d.events_moved || 0} notes, `
        + `${d.followups_moved || 0} follow-ups, ${d.sms_moved || 0} SMS moved.`,
      );
      setMergeTarget(null);
      // Navigate to the surviving lead so the admin sees the combined
      // history immediately.
      navigate(`/leads/${mergeTarget.keeper.lead_id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Merge failed');
    } finally {
      setMerging(false);
    }
  };

  return (
    <div
      className="rounded-xl border border-amber-300 bg-amber-50 p-4 flex gap-3 items-start"
      data-testid="lead-duplicate-alert"
    >
      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
        <AlertTriangle className="w-4 h-4 text-amber-700" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-amber-900">Possible duplicate</span>
          <Badge variant="outline" className="text-[10px] bg-white text-amber-800 border-amber-300">
            {count} match{count === 1 ? '' : 'es'}
          </Badge>
        </div>
        <p className="text-xs text-amber-800 mt-1">
          This contact already exists in the CRM. Review before working it so
          you don&apos;t duplicate effort or step on another agent&apos;s business.
        </p>
        {matches.length > 0 && (
          <div className="mt-3 space-y-2">
            {matches.slice(0, 5).map((m, idx) => (
              <div
                key={m.lead_id || `foreign-${idx}`}
                className="rounded-lg bg-white border border-amber-200 px-3 py-2 flex items-center justify-between gap-3"
                data-testid={`lead-duplicate-row-${idx}`}
              >
                <div className="flex-1 min-w-0 text-xs">
                  {m.foreign ? (
                    <div className="flex items-center gap-1.5 text-slate-700">
                      <ShieldAlert className="w-3.5 h-3.5 text-amber-600" />
                      <span className="italic">
                        Contact already in another agent&apos;s book
                      </span>
                    </div>
                  ) : (
                    <>
                      <div className="font-medium text-slate-900 truncate">
                        {m.first_name} {m.last_name || ''}
                      </div>
                      <div className="text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                        {(m.matched_by || []).includes('phone') && (
                          <span>📞 Same phone</span>
                        )}
                        {(m.matched_by || []).includes('email') && (
                          <span>✉️ Same email</span>
                        )}
                        {m.assigned_agent_name && (
                          <span>👤 <strong>{m.assigned_agent_name}</strong></span>
                        )}
                        {!m.assigned_agent_name && (
                          <span className="italic">Unassigned</span>
                        )}
                      </div>
                    </>
                  )}
                </div>
                {m.lead_id && (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {isKR && (
                      <Button
                        type="button" size="sm" variant="outline"
                        className="h-7 text-[11px] border-amber-400 text-amber-800 hover:bg-amber-100"
                        onClick={() => setMergeTarget(buildMergePlan(m))}
                        data-testid={`lead-duplicate-merge-${idx}`}
                      >
                        <GitMerge className="w-3 h-3 mr-1" /> Merge
                      </Button>
                    )}
                    <Link
                      to={`/leads/${m.lead_id}`}
                      className="text-[11px] font-semibold text-amber-800 hover:text-amber-900 inline-flex items-center gap-1 whitespace-nowrap"
                      data-testid={`lead-duplicate-open-${idx}`}
                    >
                      Open <ExternalLink className="w-3 h-3" />
                    </Link>
                  </div>
                )}
              </div>
            ))}
            {matches.length > 5 && (
              <div className="text-[11px] text-amber-700 italic">
                + {matches.length - 5} more
              </div>
            )}
          </div>
        )}
      </div>
      {isKR && (
        <Button
          type="button" size="sm" variant="ghost"
          className="h-7 text-amber-700 hover:text-amber-900 hover:bg-amber-100 -mr-1"
          onClick={onDismiss}
          data-testid="lead-duplicate-dismiss"
          title="Dismiss duplicate alert"
        >
          <X className="w-3.5 h-3.5" />
        </Button>
      )}

      {/* Merge confirmation dialog. The keeper (older lead) survives
          with all combined history; the source gets soft-archived. */}
      <Dialog open={!!mergeTarget} onOpenChange={(o) => !o && !merging && setMergeTarget(null)}>
        <DialogContent className="max-w-md" data-testid="lead-merge-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <GitMerge className="w-4 h-4 text-amber-600" />
              Merge leads
            </DialogTitle>
          </DialogHeader>
          {mergeTarget && (
            <div className="space-y-3 text-sm">
              <p className="text-slate-700">
                We&apos;ll merge all information from the newer lead into the
                older one. Notes, follow-ups, SMS messages and appointments
                will live on the surviving lead.
              </p>
              <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3">
                <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">
                  Survives
                </div>
                <div className="font-semibold text-emerald-900" data-testid="lead-merge-keeper">
                  {mergeTarget.keeper.first_name} {mergeTarget.keeper.last_name || ''}
                </div>
                <div className="text-[11px] text-emerald-700 font-mono">
                  {mergeTarget.keeper.lead_id}
                </div>
              </div>
              <div className="rounded-lg border border-rose-300 bg-rose-50 p-3">
                <div className="text-[10px] uppercase tracking-wider text-rose-700 font-bold">
                  Archived (soft-delete)
                </div>
                <div className="font-semibold text-rose-900" data-testid="lead-merge-source">
                  {mergeTarget.source.first_name} {mergeTarget.source.last_name || ''}
                </div>
                <div className="text-[11px] text-rose-700 font-mono">
                  {mergeTarget.source.lead_id}
                </div>
              </div>
              <p className="text-xs text-slate-500">
                Empty fields on the survivor are filled in from the archived
                lead (email, alternate phone, DOB, etc.). Existing data is
                never overwritten. This action can&apos;t be automatically
                reverted.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button
              type="button" variant="outline"
              onClick={() => setMergeTarget(null)}
              disabled={merging}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={confirmMerge}
              disabled={merging}
              data-testid="lead-merge-confirm"
            >
              {merging && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              <GitMerge className="w-4 h-4 mr-1" />
              Merge now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

